import { useMutation } from '@apollo/client';
import {
  AnalyticIdentifier,
  formatMonthYearPeriod,
  getFirstDateStringOfMonthYear,
  getLastDateStringOfMonthYear,
  MonthYearPeriod,
} from '@common/neo.tax';
import { Button, ModalActions, ModalHeader, Stack, Typography, colors, ControlledSelect } from '@ui-components/neo.tax';
import React from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';

import { TICKETING_PROVIDER_NAMES } from '@neo.tax/shared/constants';
import {
  GET_GAAP_ONBOARDING_FLOW_STATUS,
  GetGaapOnboardingFlowStatusQueryVariables,
  START_PROCESSING_TICKETING_DATA,
  StartProcessingTicketingDataMutation,
  StartProcessingTicketingDataMutationVariables,
  GET_UNFINISHED_SYNC_EVENTS_IN_PERIOD_BY_ANALYSIS_TYPE,
  AnalysisType,
  GetUnfinishedSyncEventsInPeriodByAnalysisTypeQueryVariables,
  PeriodName,
} from '@neo.tax/shared/graphql';
import { useCurrentCompanyId } from '@neo.tax/shared/hooks';
import { useProviderTokensAndSyncedProviders } from '@neo.tax/shared/hooks/useProviderTokensAndSyncedProviders';
import { useAnalyticId } from '@neo.tax/shared/state';
import { isTicketingTokenConnected } from '@neo.tax/shared/utils';

import { BuildingModernIcon } from '../get-started-hero.styled';
import { Modal } from './start-analysis-modal.styled';
import { StartAnalysisFormType } from './start-analysis-modal.types';

type StartAnalysisModalProps = {
  isOpen: boolean;
  onClose: () => void;
  analysisType: AnalysisType;
  fiscalYearId: string;
  periodName: PeriodName;
  period: MonthYearPeriod;
};

export const StartAnalysisModal = ({
  analysisType,
  fiscalYearId,
  isOpen,
  onClose,
  period,
  periodName,
}: StartAnalysisModalProps): React.ReactElement => {
  const companyId = useCurrentCompanyId();
  const { ticketingTokens: allTicketingTokens = [] } = useProviderTokensAndSyncedProviders({ fiscalYearId });
  // A GitHub App connection that has not gone ACTIVE cannot sync anything, so it is not a source an
  // analysis can be run against.
  const ticketingTokens = allTicketingTokens.filter(isTicketingTokenConnected);

  const ticketingTokenSelectAnalyticId = useAnalyticId(AnalyticIdentifier.START_ANALYSIS_MODAL_TICKETING_TOKEN_SELECT);

  const { control, handleSubmit } = useForm<StartAnalysisFormType>({
    defaultValues: {
      ticketingTokenId: ticketingTokens[0]?.id || '',
    },
  });

  const [startProcessingTicketingData, { loading }] = useMutation<
    StartProcessingTicketingDataMutation,
    StartProcessingTicketingDataMutationVariables
  >(START_PROCESSING_TICKETING_DATA);

  const onSubmit: SubmitHandler<StartAnalysisFormType> = async (values: StartAnalysisFormType) => {
    await startProcessingTicketingData({
      variables: {
        companyId,
        ticketingTokenId: values.ticketingTokenId,
        input: {
          startDateOfPeriod: getFirstDateStringOfMonthYear(period.startMonthYear),
          endDateOfPeriod: getLastDateStringOfMonthYear(period.endMonthYear),
          analysisTypes: [analysisType],
        },
      },
      awaitRefetchQueries: true,
      refetchQueries: [
        {
          query: GET_GAAP_ONBOARDING_FLOW_STATUS,
          variables: {
            companyId,
          } satisfies GetGaapOnboardingFlowStatusQueryVariables,
        },
        {
          query: GET_UNFINISHED_SYNC_EVENTS_IN_PERIOD_BY_ANALYSIS_TYPE,
          variables: {
            fiscalYearId,
            periodName,
            analysisType,
          } satisfies GetUnfinishedSyncEventsInPeriodByAnalysisTypeQueryVariables,
        },
      ],
    });

    onClose();
  };

  const handleCancel = (): void => {
    onClose();
  };

  const periodFormatted = formatMonthYearPeriod(period);

  const ticketingTokenOptions = ticketingTokens.map((token) => ({
    value: token.id,
    label: TICKETING_PROVIDER_NAMES[token.ticketingProvider],
  }));

  return (
    <Modal isOpen={isOpen}>
      <Stack as="form" direction="column" space={4} onSubmit={handleSubmit(onSubmit)}>
        <ModalHeader icon={<BuildingModernIcon />} iconBgColor={colors.white} title="Start Analysis" />

        <Typography>The currently selected period will be used for analysis.</Typography>

        <Typography textAlign="center" fontWeight="medium">
          {periodFormatted.start} - {periodFormatted.end}
        </Typography>

        <ControlledSelect
          control={control}
          id={ticketingTokenSelectAnalyticId}
          name="ticketingTokenId"
          label="Tickets to analyze"
          placeholder="Select one"
          options={ticketingTokenOptions}
          disabled={loading}
        />

        <ModalActions>
          <Button variant="white" rounded="regular" size="base" onClick={handleCancel} disabled={loading}>
            Cancel
          </Button>
          <Button variant="primary" rounded="regular" size="base" type="submit" disabled={loading}>
            Start
          </Button>
        </ModalActions>
      </Stack>
    </Modal>
  );
};
